import React, { useCallback, useLayoutEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { mergeAttributes } from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Code from "@tiptap/extension-code";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import Strike from "@tiptap/extension-strike";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import suggestion from "./suggestion";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { createfiletolink } from "../../utils/api";
import { fileToBase64 } from "../../utils/data";
import { useState } from "react";

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => element.style.width || null,
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {};
          }
          return {
            style: `width: ${attributes.width};`,
          };
        },
      },
      height: {
        default: null,
        parseHTML: (element) => element.style.height || null,
        renderHTML: (attributes) => {
          if (!attributes.height) {
            return {};
          }
          return {
            style: `height: ${attributes.height};`,
          };
        },
      },
      align: {
        default: null,
        parseHTML: (element) => {
          const margin = element.style.margin;
          if (margin === "0 auto") {
            return "center";
          } else if (margin === "0") {
            return "left";
          } else if (margin === "0 0 0 auto") {
            return "right";
          }
          return null;
        },
        renderHTML: (attributes) => {
          console.log(attributes);

          let style = "";
          if (attributes.align === "left") {
            style += "float: left; margin: 0 1em 1em 0;";
          } else if (attributes.align === "right") {
            style += "float: right; margin: 0 0 1em 1em;";
          } else if (attributes.align === "center") {
            style += "display: block; margin: 0 auto;";
          }
          return {
            style,
            align: attributes.align,
          };
        },
      },
    };
  },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const container = document.createElement("div");
      container.style.position = "relative";
      // container.style.display = "inline";
      container.style.width = "fit-content";

      const img = document.createElement("img");
      img.src = node.attrs.src;
      img.style.width = node.attrs.width || "auto";
      img.style.height = node.attrs.height || "auto";
      img.classList.add("custom-image");
      console.log(img.parentNode);
      container.appendChild(img);

      // Apply alignment
      // if (node.attrs.align === "left") {
      //   // img.style.float = "left";
      //   img.parentNode.style.margin = "0 auto 0 0 ";
      // } else if (node.attrs.align === "right") {
      //   // img.style.float = "right";
      //   img.parentNode.style.margin = "0 0 0 auto";
      // } else {
      //   // img.style.display = "block";
      //   img.parentNode.style.margin = "0 auto";
      // }

      // Create resize handle (bottom-right)
      const handle = document.createElement("div");
      handle.style.position = "absolute";
      handle.style.width = "10px";
      handle.style.height = "10px";
      handle.style.background = "rgba(0, 0, 0, 0.5)";
      handle.style.borderRadius = "50%";
      handle.style.bottom = "0";
      handle.style.right = "0";
      handle.style.cursor = "nwse-resize";
      handle.style.zIndex = "10";
      container.appendChild(handle);

      let startX, startY, startWidth, startHeight;

      const onMouseDown = (event) => {
        event.preventDefault();
        startX = event.clientX;
        startY = event.clientY;
        startWidth = img.offsetWidth;
        startHeight = img.offsetHeight;

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      };

      const onMouseMove = (event) => {
        const diffX = event.clientX - startX;
        const diffY = event.clientY - startY;

        const newWidth = Math.max(50, startWidth + diffX);
        const newHeight = Math.max(50, startHeight + diffY);

        img.style.width = newWidth + "px";
        img.style.height = newHeight + "px";
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);

        // Update the node attributes with the new dimensions
        const width = img.style.width;
        const height = img.style.height;

        editor
          .chain()
          .focus()
          .command(({ tr }) => {
            if (typeof getPos === "function") {
              tr.setNodeMarkup(getPos(), undefined, {
                ...node.attrs,
                width,
                height,
              });
            }
            return true;
          })
          .run();
      };

      handle.addEventListener("mousedown", onMouseDown);

      return {
        dom: container,
        contentDOM: null,
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) {
            return false;
          }

          img.src = updatedNode.attrs.src;
          img.style.width = updatedNode.attrs.width || "auto";
          img.style.height = updatedNode.attrs.height || "auto";

          // Update alignment if changed
          if (updatedNode.attrs.align !== node.attrs.align) {
            if (updatedNode.attrs.align === "left") {
              // img.style.float = "left";
              img.parentNode.style.margin = "0 auto 0 0 ";
            } else if (updatedNode.attrs.align === "right") {
              // img.style.float = "right";
              img.parentNode.style.margin = "0 0 0 auto";
            } else {
              // img.style.float = "";
              // img.style.display = "block";
              img.parentNode.style.margin = "0 auto";
            }
          }

          node.attrs = updatedNode.attrs;
          return true;
        },
        destroy() {
          handle.removeEventListener("mousedown", onMouseDown);
        },
      };
    };
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "img",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    ];
  },
});

export const RTE_Remasterd = ({
  formatting,
  suggestions = ["name"],
  settxt,
  setcurr_images_ids,
  setcurr_images_data,
  curr_images_data,
  preview,
  setpreview,
  preview_people,
  message_body,
  people,
  subject,
  sendMail,
}) => {
  const [open_modal, setopen_modal] = useState(false);
  const [LinkTxt, setLinkTxt] = useState("");
  const editor = useEditor({
    extensions: [
      TextAlign.configure({
        types: ["heading", "paragraph"], // You can specify which nodes can be aligned
      }),
      Document,
      Paragraph,
      Text,
      CustomImage,
      ...(suggestions.length
        ? [
            Mention.configure({
              HTMLAttributes: {
                class: "mention",
              },
              renderHTML: ({ node }) => {
                const { label, id } = node.attrs;
                return [
                  "span",
                  {
                    class: "dynamic-variable-button",
                  },
                  `{${label || id}}`,
                ];
              },
              suggestion: {
                ...suggestion,
                items: ({ query, editor }) => {
                  return editor.options.editorProps.items?.filter((item) => {
                    let t_item = item.toLowerCase();
                    let t_query = query.toLowerCase();

                    return t_item.includes(t_query);
                  });
                },
              },
            }),
          ]
        : []),
      Placeholder.configure({
        placeholder: "Write something …",
      }),
      ...(formatting
        ? [
            Bold.configure({
              HTMLAttributes: {
                class: "custom_bold",
              },
            }),
            Code,
            Strike,
            Italic,
            Link.configure({
              openOnClick: true,
              autolink: false,
              defaultProtocol: "https",
              linkOnPaste: false,
            }),
            Highlight.configure({ multicolor: true }),
            Underline,
            TextStyle,
            Color,
            BulletList,
            OrderedList,
            ListItem,
          ]
        : []),
    ],
    content: `<p></p>`,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      settxt(html);
      localStorage.setItem("mailData", html);
    },
  });

  // Function to simulate image upload and return a URL
  const uploadImage = async (file) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      resolve(url);
    });
  };

  const addImage = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (editor) {
          event.target.value = null;
          createfiletolink(
            { base64: reader.result },
            (success) => {
              if (success?.url) {
                editor
                  .chain()
                  .focus()
                  .setImage({
                    src: success.url,
                    width: "300px",
                    height: "auto",
                  })
                  .run();
              } else {
              }
            },
            () => {},
            () => {}
          );
        }
      };
      reader.onerror = (error) => {};
      reader.readAsDataURL(file);
    }
  };

  const updateImageAlignment = (alignment) => {
    if (!editor) return;

    const attrs = editor.getAttributes("image");
    if (attrs && Object.keys(attrs).length > 0) {
      editor
        .chain()
        .focus()
        .updateAttributes("image", { align: alignment })
        .run();
    }
  };

  useLayoutEffect(() => {
    // let dataFromLocal = localStorage.getItem("mailData");

    // if (editor && dataFromLocal) {
    //   editor.commands.setContent(dataFromLocal, false, {
    //     preserveWhitespace: "full",
    //   });
    //   settxt(dataFromLocal);
    // }
    if (editor && suggestions.length > 0) {
      editor.setOptions({
        editorProps: {
          items:
            suggestions?.filter(
              (el) => !["_id", "Photo", "isLiked", "firmId"].includes(el)
            ) || [],
        },
      });
    }

    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [suggestions, editor]);

  const setLink = () => {
    // const url = window.prompt("URL", previousUrl);
    // if (url === null) {
    //   return;
    // }
    // if (url === "") {
    //   editor.chain().focus().extendMarkRange("link").unsetLink().run();
    //   return;
    // }
    // editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <>
      <div className="rte_body">
        {open_modal == "setLink" ? (
          <div className="setUrl_modal">
            <div>
              <input
                placeholder="Enter URL"
                value={LinkTxt}
                onChange={({ target: { value } }) => setLinkTxt(value)}
              />
              <div className="btn_gorup">
                <button
                  className={
                    "save" +
                    (LinkTxt?.trim() == "" ||
                    LinkTxt?.trim() == editor.getAttributes("link").href
                      ? " disabled"
                      : "")
                  }
                  onClick={() => {
                    const previousUrl = editor.getAttributes("link").href;
                    if (
                      LinkTxt?.trim() == "" ||
                      LinkTxt?.trim() == previousUrl
                    ) {
                    }
                    const url = LinkTxt;

                    if (url === null) {
                      return;
                    }

                    if (url === "") {
                      editor
                        .chain()
                        .focus()
                        .extendMarkRange("link")
                        .unsetLink()
                        .run();
                      return;
                    }

                    editor
                      .chain()
                      .focus()
                      .extendMarkRange("link")
                      .setLink({ href: url })
                      .run();
                    setopen_modal("");
                    setLinkTxt("");
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setopen_modal("");
                    setLinkTxt("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          ""
        )}
        <div
          className="rich_text_editor"
          onClick={() => {
            if (!editor.isFocused) {
              editor.commands.focus("end");
            }
          }}
        >
          <EditorContent editor={editor} className="editor_main" />

          <div className="image_group">
            {curr_images_data?.map((el) => (
              <div>
                {el?.link ? (
                  <>
                    <div
                      className="name"
                      onClick={() => {
                        window.open(el.link, "_blank");
                      }}
                    >
                      {el?.name}.
                      {el?.link ? el?.link?.split(".")?.reverse()[0] : ""}
                    </div>

                    <div
                      className="delete"
                      onClick={() => {
                        setcurr_images_data((prev) => [
                          ...prev.filter((el1) => el1.name != el.name),
                        ]);
                        setcurr_images_ids((prev) =>
                          prev.filter((el1) => el1 !== el)
                        );
                        let data = curr_images_data?.filter(
                          ({ new_id: id }) => id !== el.new_id
                        );
                        localStorage.setItem(
                          "mailAttach",
                          JSON.stringify(data)
                        );
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        width="12"
                        height="12"
                        x="0"
                        y="0"
                        viewBox="0 0 512 512"
                        class="hovered-paths"
                      >
                        <g>
                          <path
                            d="m292.2 256 109.9-109.9c10-10 10-26.2 0-36.2s-26.2-10-36.2 0L256 219.8 146.1 109.9c-10-10-26.2-10-36.2 0s-10 26.2 0 36.2L219.8 256 109.9 365.9c-10 10-10 26.2 0 36.2 5 5 11.55 7.5 18.1 7.5s13.1-2.5 18.1-7.5L256 292.2l109.9 109.9c5 5 11.55 7.5 18.1 7.5s13.1-2.5 18.1-7.5c10-10 10-26.2 0-36.2z"
                            fill="#000000"
                            opacity="1"
                            data-original="#000000"
                            class="hovered-path"
                          ></path>
                        </g>
                      </svg>
                    </div>
                  </>
                ) : (
                  <>
                    <svg
                      style={{ transform: "rotate(-60deg)" }}
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="circle_svg_api"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="9"
                        cy="9"
                        r="8"
                        stroke="#777777"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        strokeDashoffset={70 - (el?.loading / 100) * 56}
                        strokeDasharray="56"
                      />
                    </svg>

                    <div className="name">{el?.name}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
        <div
          className="preview_txt"
          onClick={() => setpreview((prev) => !prev)}
        >
          <svg
            width="34"
            height="80"
            viewBox="0 0 34 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_4328_133)">
              <path
                d="M10 80C4.47715 80 0 75.5228 0 70L0 10C0 4.47715 4.47715 0 10 0H34L34 80H10Z"
                fill="#D9D9D9"
              />
              <path
                d="M25 68.5909H13.3636V64.6591C13.3636 63.7462 13.5284 63 13.858 62.4205C14.1837 61.8371 14.625 61.4053 15.1818 61.125C15.7386 60.8447 16.3598 60.7045 17.0455 60.7045C17.7311 60.7045 18.3542 60.8447 18.9148 61.125C19.4754 61.4015 19.9223 61.8295 20.2557 62.4091C20.5852 62.9886 20.75 63.7311 20.75 64.6364V67.4545H19.5V64.6818C19.5 64.0568 19.392 63.5549 19.1761 63.1761C18.9602 62.7973 18.6686 62.5227 18.3011 62.3523C17.9299 62.178 17.5114 62.0909 17.0455 62.0909C16.5795 62.0909 16.1629 62.178 15.7955 62.3523C15.428 62.5227 15.1402 62.7992 14.9318 63.1818C14.7197 63.5644 14.6136 64.072 14.6136 64.7045V67.1818H25V68.5909ZM25 58.6165H16.2727V57.321H17.5909V57.2301C17.1591 57.071 16.8087 56.7831 16.5398 56.3665C16.2708 55.9498 16.1364 55.4801 16.1364 54.9574C16.1364 54.8589 16.1383 54.7358 16.142 54.5881C16.1458 54.4403 16.1515 54.3286 16.1591 54.2528H17.5227C17.5114 54.2983 17.4943 54.4025 17.4716 54.5653C17.4451 54.7244 17.4318 54.893 17.4318 55.071C17.4318 55.4953 17.5208 55.8741 17.6989 56.2074C17.8731 56.5369 18.1155 56.7983 18.4261 56.9915C18.733 57.1809 19.0833 57.2756 19.4773 57.2756H25V58.6165ZM25.1818 49.2699C25.1818 50.1108 24.9962 50.8362 24.625 51.446C24.25 52.0521 23.7273 52.5199 23.0568 52.8494C22.3826 53.1752 21.5985 53.3381 20.7045 53.3381C19.8106 53.3381 19.0227 53.1752 18.3409 52.8494C17.6553 52.5199 17.1212 52.0616 16.7386 51.4744C16.3523 50.8835 16.1591 50.1941 16.1591 49.4062C16.1591 48.9517 16.2348 48.5028 16.3864 48.0597C16.5379 47.6165 16.7841 47.2131 17.125 46.8494C17.4621 46.4858 17.9091 46.196 18.4659 45.9801C19.0227 45.7642 19.7083 45.6562 20.5227 45.6562H21.0909V52.3835H19.9318V47.0199C19.4394 47.0199 19 47.1184 18.6136 47.3153C18.2273 47.5085 17.9223 47.785 17.6989 48.1449C17.4754 48.5009 17.3636 48.9214 17.3636 49.4062C17.3636 49.9403 17.4962 50.4025 17.7614 50.7926C18.0227 51.179 18.3636 51.4763 18.7841 51.6847C19.2045 51.893 19.6553 51.9972 20.1364 51.9972H20.9091C21.5682 51.9972 22.1269 51.8835 22.5852 51.6562C23.0398 51.4252 23.3864 51.1051 23.625 50.696C23.8598 50.2869 23.9773 49.8116 23.9773 49.2699C23.9773 48.9176 23.928 48.5994 23.8295 48.3153C23.7273 48.0275 23.5758 47.7794 23.375 47.571C23.1705 47.3627 22.9167 47.2017 22.6136 47.0881L22.9773 45.7926C23.4167 45.929 23.803 46.1581 24.1364 46.4801C24.4659 46.8021 24.7235 47.1998 24.9091 47.6733C25.0909 48.1468 25.1818 48.679 25.1818 49.2699ZM16.2727 36.7457L25 39.973V41.3366L16.2727 44.5639V43.1094L23.2273 40.7003V40.6094L16.2727 38.2003V36.7457ZM25 34.9759H16.2727L16.2727 33.6349H25V34.9759ZM14.8182 34.294C14.8182 34.5554 14.7292 34.7808 14.5511 34.9702C14.3731 35.1558 14.1591 35.2486 13.9091 35.2486C13.6591 35.2486 13.4451 35.1558 13.267 34.9702C13.089 34.7808 13 34.5554 13 34.294C13 34.0327 13.089 33.8092 13.267 33.6236C13.4451 33.4342 13.6591 33.3395 13.9091 33.3395C14.1591 33.3395 14.3731 33.4342 14.5511 33.6236C14.7292 33.8092 14.8182 34.0327 14.8182 34.294ZM25.1818 27.5199C25.1818 28.3608 24.9962 29.0862 24.625 29.696C24.25 30.3021 23.7273 30.7699 23.0568 31.0994C22.3826 31.4252 21.5985 31.5881 20.7045 31.5881C19.8106 31.5881 19.0227 31.4252 18.3409 31.0994C17.6553 30.7699 17.1212 30.3116 16.7386 29.7244C16.3523 29.1335 16.1591 28.4441 16.1591 27.6562C16.1591 27.2017 16.2348 26.7528 16.3864 26.3097C16.5379 25.8665 16.7841 25.4631 17.125 25.0994C17.4621 24.7358 17.9091 24.446 18.4659 24.2301C19.0227 24.0142 19.7083 23.9062 20.5227 23.9062H21.0909L21.0909 30.6335H19.9318L19.9318 25.2699C19.4394 25.2699 19 25.3684 18.6136 25.5653C18.2273 25.7585 17.9223 26.035 17.6989 26.3949C17.4754 26.7509 17.3636 27.1714 17.3636 27.6562C17.3636 28.1903 17.4962 28.6525 17.7614 29.0426C18.0227 29.429 18.3636 29.7263 18.7841 29.9347C19.2045 30.143 19.6553 30.2472 20.1364 30.2472H20.9091C21.5682 30.2472 22.1269 30.1335 22.5852 29.9062C23.0398 29.6752 23.3864 29.3551 23.625 28.946C23.8598 28.5369 23.9773 28.0616 23.9773 27.5199C23.9773 27.1676 23.928 26.8494 23.8295 26.5653C23.7273 26.2775 23.5758 26.0294 23.375 25.821C23.1705 25.6127 22.9167 25.4517 22.6136 25.3381L22.9773 24.0426C23.4167 24.179 23.803 24.4081 24.1364 24.7301C24.4659 25.0521 24.7235 25.4498 24.9091 25.9233C25.0909 26.3968 25.1818 26.929 25.1818 27.5199ZM25 19.8892L16.2727 22.5483V21.1392L22.9545 19.2528V19.1619L16.2727 17.2983V15.8665L22.9318 14.0256V13.9347L16.2727 12.0483V10.6392L25 13.2983V14.6165L18.2955 16.5256V16.6619L25 18.571V19.8892Z"
                fill="black"
              />
            </g>
            <defs>
              <clipPath id="clip0_4328_133">
                <path
                  d="M10 80C4.47715 80 0 75.5228 0 70L0 10C0 4.47715 4.47715 0 10 0H34L34 80H10Z"
                  fill="white"
                />
              </clipPath>
            </defs>
          </svg>
        </div>
        <div
          className="preview"
          style={
            preview
              ? {
                  width: "100%",
                }
              : {
                  width: "0",
                }
          }
        >
          {preview && (
            <div className="preview_body">
              <div
                className="tiptap"
                dangerouslySetInnerHTML={{
                  __html: (() =>
                    Object.keys(preview_people).reduce(
                      (acc, curr) =>
                        acc.replaceAll(`{${curr}}`, preview_people[curr]),
                      message_body
                    ))(),
                }}
              ></div>
            </div>
          )}
        </div>
      </div>
      {formatting ? (
        <div className="control-group" onClick={(e) => e.stopPropagation()}>
          <div
            className="send_btn"
            active={String(
              people.length > 0 &&
                subject.length > 2 &&
                message_body.length > 10
            )}
            onClick={sendMail}
          >
            Send
          </div>
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive("bold") ? "is-active" : ""}
          >
            <img src="https://www.gstatic.com/images/icons/material/system_gm/1x/format_bold_black_20dp.png" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive("italic") ? "is-active" : ""}
          >
            <img src="https://www.gstatic.com/images/icons/material/system_gm/1x/format_italic_black_20dp.png" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive("underline") ? "is-active" : ""}
          >
            <img src="https://www.gstatic.com/images/icons/material/system_gm/1x/format_underlined_black_20dp.png" />
          </button>
          {editor.isActive({ textAlign: "left" }) ||
          editor.isActive({ textAlign: "center" }) ||
          editor.isActive({ textAlign: "right" }) ||
          editor.isActive({ textAlign: "justify" }) ? (
            <>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
                className={
                  editor.isActive({ textAlign: "left" }) ? "is-active" : ""
                }
              >
                <svg width="20" height="20" x="0" y="0" viewBox="0 0 330 330">
                  <g>
                    <path
                      d="M15 60h300c8.284 0 15-6.716 15-15s-6.716-15-15-15H15C6.716 30 0 36.716 0 45s6.716 15 15 15zM15 120h220c8.284 0 15-6.716 15-15s-6.716-15-15-15H15c-8.284 0-15 6.716-15 15s6.716 15 15 15zM315 150H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM315 270H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM15 240h220c8.284 0 15-6.716 15-15s-6.716-15-15-15H15c-8.284 0-15 6.716-15 15s6.716 15 15 15z"
                      fill="#000000"
                      opacity="1"
                      data-original="#000000"
                    ></path>
                  </g>
                </svg>
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
                className={
                  editor.isActive({ textAlign: "center" }) ? "is-active" : ""
                }
              >
                <svg width="20" height="20" x="0" y="0" viewBox="0 0 330 330">
                  <g>
                    <path
                      d="M15 60h300c8.284 0 15-6.716 15-15s-6.716-15-15-15H15C6.716 30 0 36.716 0 45s6.716 15 15 15zM55 90c-8.284 0-15 6.716-15 15s6.716 15 15 15h220c8.284 0 15-6.716 15-15s-6.716-15-15-15H55zM315 150H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM315 270H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM275 240c8.284 0 15-6.716 15-15s-6.716-15-15-15H55c-8.284 0-15 6.716-15 15s6.716 15 15 15h220z"
                      fill="#000000"
                      opacity="1"
                      data-original="#000000"
                    ></path>
                  </g>
                </svg>
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
                className={
                  editor.isActive({ textAlign: "right" }) ? "is-active" : ""
                }
              >
                <svg width="20" height="20" x="0" y="0" viewBox="0 0 330 330">
                  <g>
                    <path
                      d="M315 270H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM315 210H95c-8.284 0-15 6.716-15 15s6.716 15 15 15h220c8.284 0 15-6.716 15-15s-6.716-15-15-15zM315 150H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM15 60h300c8.284 0 15-6.716 15-15s-6.716-15-15-15H15C6.716 30 0 36.716 0 45s6.716 15 15 15zM315 90H95c-8.284 0-15 6.716-15 15s6.716 15 15 15h220c8.284 0 15-6.716 15-15s-6.716-15-15-15z"
                      fill="#000000"
                      opacity="1"
                      data-original="#000000"
                      class=""
                    ></path>
                  </g>
                </svg>
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("justify").run()
                }
                className={
                  editor.isActive({ textAlign: "justify" }) ? "is-active" : ""
                }
              >
                <svg
                  width="20"
                  height="20"
                  x="0"
                  y="0"
                  viewBox="0 0 329.999 329.999"
                >
                  <g>
                    <path
                      d="M15 60h299.999c8.284 0 15-6.716 15-15s-6.716-15-15-15H15C6.716 30 0 36.716 0 45s6.716 15 15 15zM314.999 90H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h299.999c8.284 0 15-6.716 15-15s-6.715-15-15-15zM314.999 150H15c-8.284 0-15 6.716-15 15 0 8.284 6.716 15 15 15h299.999c8.284 0 15-6.716 15-15 0-8.284-6.715-15-15-15zM314.999 270H15c-8.284 0-15 6.716-15 15 0 8.284 6.716 15 15 15h299.999c8.284 0 15-6.716 15-15 0-8.284-6.715-15-15-15zM314.999 210H15c-8.284 0-15 6.716-15 15 0 8.284 6.716 15 15 15h299.999c8.284 0 15-6.716 15-15 0-8.284-6.715-15-15-15z"
                      fill="#000000"
                      opacity="1"
                      data-original="#000000"
                      class=""
                    ></path>
                  </g>
                </svg>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => updateImageAlignment("left")}
                className={
                  editor.isActive("image", { align: "left" }) ? "is-active" : ""
                }
              >
                <svg width="20" height="20" x="0" y="0" viewBox="0 0 330 330">
                  <g>
                    <path
                      d="M15 60h300c8.284 0 15-6.716 15-15s-6.716-15-15-15H15C6.716 30 0 36.716 0 45s6.716 15 15 15zM15 120h220c8.284 0 15-6.716 15-15s-6.716-15-15-15H15c-8.284 0-15 6.716-15 15s6.716 15 15 15zM315 150H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM315 270H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM15 240h220c8.284 0 15-6.716 15-15s-6.716-15-15-15H15c-8.284 0-15 6.716-15 15s6.716 15 15 15z"
                      fill="#000000"
                      opacity="1"
                      data-original="#000000"
                    ></path>
                  </g>
                </svg>
              </button>
              <button onClick={() => updateImageAlignment("center")}>
                <svg width="20" height="20" x="0" y="0" viewBox="0 0 330 330">
                  <g>
                    <path
                      d="M15 60h300c8.284 0 15-6.716 15-15s-6.716-15-15-15H15C6.716 30 0 36.716 0 45s6.716 15 15 15zM55 90c-8.284 0-15 6.716-15 15s6.716 15 15 15h220c8.284 0 15-6.716 15-15s-6.716-15-15-15H55zM315 150H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM315 270H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM275 240c8.284 0 15-6.716 15-15s-6.716-15-15-15H55c-8.284 0-15 6.716-15 15s6.716 15 15 15h220z"
                      fill="#000000"
                      opacity="1"
                      data-original="#000000"
                    ></path>
                  </g>
                </svg>
              </button>
              <button onClick={() => updateImageAlignment("right")}>
                <svg width="20" height="20" x="0" y="0" viewBox="0 0 330 330">
                  <g>
                    <path
                      d="M315 270H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM315 210H95c-8.284 0-15 6.716-15 15s6.716 15 15 15h220c8.284 0 15-6.716 15-15s-6.716-15-15-15zM315 150H15c-8.284 0-15 6.716-15 15s6.716 15 15 15h300c8.284 0 15-6.716 15-15s-6.716-15-15-15zM15 60h300c8.284 0 15-6.716 15-15s-6.716-15-15-15H15C6.716 30 0 36.716 0 45s6.716 15 15 15zM315 90H95c-8.284 0-15 6.716-15 15s6.716 15 15 15h220c8.284 0 15-6.716 15-15s-6.716-15-15-15z"
                      fill="#000000"
                      opacity="1"
                      data-original="#000000"
                      class=""
                    ></path>
                  </g>
                </svg>
              </button>
            </>
          )}
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive("bulletList") ? "is-active" : ""}
          >
            <img src="https://www.gstatic.com/images/icons/material/system_gm/1x/format_list_bulleted_black_20dp.png" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive("bulletList") ? "is-active" : ""}
          >
            <img src="https://www.gstatic.com/images/icons/material/system_gm/1x/format_list_numbered_black_20dp.png" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={editor.isActive("strike") ? "is-active" : ""}
          >
            <img src="https://www.gstatic.com/images/icons/material/system_gm/1x/strikethrough_s_black_20dp.png" />
          </button>
          <label className="highlight_group" htmlFor="text-color-input">
            <svg
              style={{ width: "calc(100% - 15px)" }}
              xmlns="http://www.w3.org/2000/svg"
              width="362"
              height="404"
              viewBox="0 0 362 404"
              fill="none"
            >
              <path
                d="M116.979 325.981H244.102L267.093 403.894H361.261L230.81 0.972168H130.271L0.111328 403.894H94.277L116.979 325.981ZM179.578 110.05H181.23L223.33 252.52H137.755L179.578 110.05Z"
                fill="black"
              />
            </svg>
            <input
              id="text-color-input"
              type="color"
              onInput={(event) =>
                editor.chain().focus().setColor(event.target.value).run()
              }
              value={editor.getAttributes("textStyle").color}
              data-testid="setColor"
            />
          </label>
          <label className="texthightlight_group">
            <svg
              style={{ width: "calc(100% - 13px)" }}
              xmlns="http://www.w3.org/2000/svg"
              width="417"
              height="383"
              viewBox="0 0 417 383"
              fill="none"
            >
              <path
                d="M91.0091 228.6L64.6091 255V319L0.289062 383H64.6091L96.7691 351H161.089L187.489 324.6L91.0091 228.6ZM402.209 43.1601L373.889 15.0001C369.419 10.4283 364.08 6.79574 358.186 4.31569C352.293 1.83564 345.963 0.558105 339.569 0.558105C333.175 0.558105 326.845 1.83564 320.952 4.31569C315.058 6.79574 309.719 10.4283 305.249 15.0001L102.369 217.4L198.369 313.4L402.209 111C411.198 102 416.247 89.8002 416.247 77.0801C416.247 64.3601 411.198 52.1602 402.209 43.1601Z"
                fill="black"
              />
            </svg>
            <input
              type="color"
              onInput={(event) =>
                editor
                  .chain()
                  .focus()
                  .toggleHighlight({ color: event.target.value })
                  .run()
              }
              value={editor.getAttributes("textHighlight").color}
            />
          </label>
          {!editor.isActive("link") || true ? (
            <button
              onClick={() => {
                const previousUrl = editor.getAttributes("link").href;
                console.log(previousUrl);

                setLinkTxt(previousUrl);
                setopen_modal("setLink");
              }}
              className={editor.isActive("link") ? "is-active" : ""}
            >
              <svg
                width="20"
                height="20"
                style={{
                  padding: 3,
                }}
                x="0"
                y="0"
                viewBox="0 0 277.279 277.279"
              >
                <g>
                  <path
                    d="m149.245 191.671-42.425 42.426-.001.001-.001.001c-17.544 17.545-46.092 17.546-63.638 0-8.5-8.5-13.18-19.801-13.18-31.82 0-12.018 4.68-23.317 13.177-31.817l.003-.003 42.425-42.426c5.857-5.858 5.857-15.356-.001-21.213-5.857-5.857-15.355-5.857-21.213 0l-42.425 42.426-.009.01C7.798 163.42 0 182.251 0 202.279c0 20.033 7.801 38.867 21.967 53.033C36.589 269.933 55.794 277.244 75 277.244c19.206 0 38.412-7.311 53.032-21.932v-.001l.001-.001 42.425-42.426c5.857-5.857 5.857-15.355-.001-21.213-5.856-5.857-15.353-5.857-21.212 0zM277.279 75c0-20.033-7.802-38.867-21.968-53.033-29.243-29.242-76.824-29.241-106.065 0l-.004.005-42.424 42.423c-5.858 5.857-5.858 15.356 0 21.213a14.952 14.952 0 0 0 10.607 4.394c3.838 0 7.678-1.465 10.606-4.394l42.424-42.423.005-.005c17.544-17.544 46.092-17.545 63.638 0 8.499 8.5 13.181 19.801 13.181 31.82 0 12.018-4.68 23.317-13.178 31.817l-.003.003-42.425 42.426c-5.857 5.857-5.857 15.355.001 21.213a14.954 14.954 0 0 0 10.606 4.394c3.839 0 7.678-1.465 10.607-4.394l42.425-42.426.009-.01C269.48 113.859 277.279 95.028 277.279 75z"
                    fill="#000000"
                    opacity="1"
                    data-original="#000000"
                    class=""
                  ></path>
                  <path
                    d="M85.607 191.671a14.954 14.954 0 0 0 10.606 4.394c3.839 0 7.678-1.465 10.607-4.394l84.852-84.852c5.858-5.857 5.858-15.355 0-21.213-5.857-5.857-15.355-5.857-21.213 0l-84.852 84.851c-5.858 5.859-5.858 15.357 0 21.214z"
                    fill="#000000"
                    opacity="1"
                    data-original="#000000"
                    class=""
                  ></path>
                </g>
              </svg>
            </button>
          ) : (
            <button
              onClick={() => editor.chain().focus().unsetLink().run()}
              disabled={!editor.isActive("link")}
            >
              Unset link
            </button>
          )}
          <div className="toolbar_divider"></div>
          <label className="attach_file" htmlFor="addimage">
            <img src="https://ssl.gstatic.com/ui/v1/icons/mail/gm3/1x/image_baseline_nv700_20dp.png" />
            <input
              type="file"
              accept="image/*"
              onChange={addImage}
              id="addimage"
            />
          </label>
          {/* <div className="attachimage_actions">
            <button onClick={() => updateImageAlignment("left")}>
              Align Left
            </button>
            <button onClick={() => updateImageAlignment("center")}>
              Align Center
            </button>
            <button onClick={() => updateImageAlignment("right")}>
              Align Right
            </button>
          </div> */}
          <div className="toolbar_divider"></div>
          <label className="attach_file" htmlFor={"order_notes"}>
            <input
              type="file"
              id={"order_notes"}
              capture={true}
              // accept="image/*"
              onClick={(e) => {
                e.target.value = null;
              }}
              onChange={async (e) => {
                // convert_to_base64(e, el.type);

                const fileData = await fileToBase64(e);
                let new_id = "imgid" + new Date().valueOf().toString();
                let new_image = {
                  name: fileData.name,
                  base64: fileData.base64,
                  link: "",
                  loading: 0,
                  new_id: new_id,
                };
                setcurr_images_ids((prev) => [...prev, new_id]);
                setcurr_images_data((prev) => [...prev, new_image]);
                createfiletolink(
                  fileData,
                  (success) => {
                    if (success?.url) {
                      localStorage.setItem(
                        "mailAttach",
                        JSON.stringify([
                          ...curr_images_data.filter(
                            ({ new_id: id }) => id !== new_id
                          ),
                          {
                            ...curr_images_data.find(
                              ({ new_id: id }) => id === new_id
                            ),
                            link: success.url,
                            base64: "",
                            loading: 100,
                            name: new_image.name,
                            new_id: new_id,
                          },
                        ])
                      );
                      setcurr_images_data((prev) => [
                        ...prev.filter(({ new_id: id }) => id !== new_id), // correct comparison
                        {
                          ...prev.find(({ new_id: id }) => id === new_id), // find the correct image to update
                          link: success.url,
                          base64: "", // remove base64 as the link is now set
                        },
                      ]);
                    } else {
                    }
                  },
                  (error) => {},
                  (loadingPercentage) => {
                    setcurr_images_data((prev) => [
                      ...prev.filter(({ new_id: id }) => id !== new_id), // correct comparison
                      {
                        ...prev.find(({ new_id: id }) => id === new_id), // find the correct image to update
                        loading: loadingPercentage,
                      },
                    ]);
                  }
                );
              }}
              multiple={false}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              version="1.1"
              xlink="http://www.w3.org/1999/xlink"
              width="16"
              height="16"
              x="0"
              y="0"
              viewBox="0 0 12 12"
              space="preserve"
              class=""
            >
              <g>
                <path
                  d="M6 11.875A3.378 3.378 0 0 1 2.625 8.5v-6C2.625 1.19 3.69.125 5 .125S7.375 1.19 7.375 2.5V8c0 .758-.617 1.375-1.375 1.375S4.625 8.758 4.625 8V3.5a.375.375 0 0 1 .75 0V8a.626.626 0 0 0 1.25 0V2.5C6.625 1.604 5.896.875 5 .875S3.375 1.604 3.375 2.5v6c0 1.447 1.178 2.625 2.625 2.625S8.625 9.947 8.625 8.5v-5a.375.375 0 0 1 .75 0v5A3.378 3.378 0 0 1 6 11.875z"
                  fill="#333333"
                  opacity="1"
                  data-original="#333333"
                ></path>
              </g>
            </svg>
          </label>
        </div>
      ) : null}
    </>
  );
};
